/**
 * Form Builder Component
 * Main component for building dynamic forms with improved architecture
 */
import {
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
  useCallback
} from 'react'
import { Box, Grid2 } from '@mui/material'
import { useForm, FormProvider } from 'react-hook-form'
import FormField from './FormField'
import { isFieldVisible } from './utils/dynamicFields'
import {
  FormBuilderProps,
  FormChange,
  FormBuilderRef,
  FormFieldConfig
} from './types'
import { FormContextProvider } from './FormContext'
import { shouldReloadDataSource } from './utils/dataSource'
import {
  useDataSource,
  useLinkageRules,
  useFormValues,
  useWatchedFields
} from './hooks'

const FormBuilder = forwardRef<FormBuilderRef, FormBuilderProps>(
  (
    {
      config,
      gridSpacing = 2,
      initialValues = {},
      validationMode,
      onChange,
      onDataSourcesReady,
      onBlur,
      readOnly = false,
      children,
      renderFields = true
    },
    ref
  ) => {
    const flattenErrors = useCallback(
      (errors: Record<string, any>, parentPath = ''): Record<string, string> => {
        const flattened: Record<string, string> = {}

        Object.entries(errors).forEach(([key, value]) => {
          if (!value || typeof value !== 'object') {
            return
          }

          const fieldPath = parentPath ? `${parentPath}.${key}` : key
          const maybeMessage = value.message
          const maybeType = value.type

          if (typeof maybeType === 'string' && typeof maybeMessage === 'string') {
            flattened[fieldPath] = maybeMessage
            return
          }

          Object.assign(flattened, flattenErrors(value, fieldPath))
        })

        return flattened
      },
      []
    )

    // Process config to apply readOnly property to fields if needed
    const processedConfig = useMemo(() => {
      if (!readOnly) return config

      // Deep clone the config to avoid mutating the original
      const newConfig = {
        ...config,
        fields: config.fields.map(field => ({ ...field }))
      }

      // If readOnly is boolean true, set all fields to readOnly
      if (readOnly === true) {
        newConfig.fields.forEach(field => {
          field.readOnly = true
        })
      }

      return newConfig
    }, [config, readOnly])

    // Setup form with react-hook-form
    const methods = useForm({
      defaultValues: initialValues,
      ...(validationMode ? { mode: validationMode } : {})
    })

    const {
      control,
      handleSubmit,
      watch,
      formState,
      reset,
      setValue,
      getValues,
      setError,
      clearErrors
    } = methods

    // Get all form values
    const allValues = watch()

    // Use direct RHF errors since externalErrors prop is removed
    // We can rely directly on formState.errors because RHF handles re-renders on error changes
    const mergedErrors = formState.errors

    // Use data source management hook
    const { dataSourcesState, reloadDataSource, getDataSource } = useDataSource(
      {
        dataSources: processedConfig.dataSources,
        fields: processedConfig.fields,
        getValues,
        setValue,
        initialValues,
        onDataSourcesReady
      }
    )

    // Use form values management hook
    const {
      filterConfiguredValues,
      applyAllTransforms,
      getFormattedValues,
      getFormChanges,
      handleValueChange,
      resetPreviousValues
    } = useFormValues({
      fields: processedConfig.fields,
      initialValues,
      allValues,
      onChange
    })

    // Use linkage rules management hook
    const { applyLinkageRules, processLinkageRules } = useLinkageRules({
      linkageRules: processedConfig.linkageRules,
      dataSources: processedConfig.dataSources,
      getValues,
      setValue,
      clearErrors,
      reloadDataSource
    })

    // Callback for when watched values change
    const handleWatchedValuesChange = useCallback(
      (currentValues: Record<string, any>, prevValues: Record<string, any>) => {
        // Process linkage rules
        processLinkageRules(currentValues, prevValues)

        // Handle data source reloading
        if (processedConfig.dataSources) {
          const reloadDataSources = async () => {
            for (const dataSource of processedConfig.dataSources || []) {
              if (
                shouldReloadDataSource(dataSource, prevValues, currentValues)
              ) {
                await reloadDataSource(dataSource.key)
              }
            }
          }
          reloadDataSources()
        }
      },
      [processedConfig.dataSources, processLinkageRules, reloadDataSource]
    )

    // Use watched fields management hook
    useWatchedFields({
      fields: processedConfig.fields,
      dataSources: processedConfig.dataSources,
      linkageRules: processedConfig.linkageRules,
      control,
      onWatchedValuesChange: handleWatchedValuesChange
    })

    // Handle form value changes for onChange callback
    useEffect(() => {
      const subscription = watch((value, { name }) => {
        if (name && onChange) {
          handleValueChange(name, value)
        }
      })

      return () => subscription.unsubscribe()
    }, [watch, onChange, handleValueChange])

    /**
     * Handle form reset
     */
    const handleReset = useCallback(() => {
      reset(initialValues)
      // reset() dispatches with name === undefined, so the watch bridge above never
      // fires; re-seed the baseline here or the next re-selection of a pre-reset
      // value is swallowed by the equality check in useFormValues.
      // Read it back with getValues(), not initialValues: reset() also clears the
      // fields that initialValues does not mention, and the watch subscription
      // will later compare against what the form actually holds.
      resetPreviousValues(getValues())
    }, [reset, initialValues, getValues, resetPreviousValues])

    /**
     * Get field grid props for responsive layout
     */
    const getFieldGridProps = useCallback((fieldConfig: FormFieldConfig) => {
      const defaultProps = { xs: 12, sm: 6 }

      if (!fieldConfig.size) {
        return { size: defaultProps }
      }

      return { size: fieldConfig.size }
    }, [])

    /**
     * Render form fields
     */
    const renderConfigFields = useCallback(() => {
      if (!renderFields) return null

      const fields = processedConfig.fields
        .map(fieldConfig => {
          // Check visibility BEFORE rendering Grid2 to avoid empty space
          if (!isFieldVisible(fieldConfig, allValues)) {
            return null
          }
          const gridProps = getFieldGridProps(fieldConfig)

          // Calculate field type for key generation
          // This ensures the field component is remounted when type changes
          const fieldType =
            typeof fieldConfig.type === 'function'
              ? fieldConfig.type(allValues)
              : fieldConfig.type

          // Use fieldName + fieldType as key to force remount when type changes
          const componentKey = `${fieldConfig.name}-${fieldType}`

          return (
            <Grid2 {...(gridProps as any)} key={fieldConfig.name}>
              <FormField
                key={componentKey}
                config={fieldConfig}
                control={control}
                errors={mergedErrors}
                values={allValues}
                readOnly={readOnly}
              />
            </Grid2>
          )
        })
        .filter((field): field is React.ReactElement => field !== null)

      return fields.length > 0 ? fields : null
    }, [
      renderFields,
      processedConfig.fields,
      getFieldGridProps,
      control,
      mergedErrors,
      allValues,
      readOnly
    ])

    // Expose functions via ref
    useImperativeHandle(
      ref,
      () => ({
        // Get all form values (filtered and transformed)
        getAllValues: () => getFormattedValues(),

        // Get specific field values
        getFieldValues: (fieldNames: string | string[]) => {
          if (Array.isArray(fieldNames)) {
            return getValues(fieldNames)
          }
          return getValues(fieldNames)
        },

        // Reset form to initial values
        resetForm: () => handleReset(),

        // Get all form changes
        getChangedFields: () => getFormChanges(),

        // Validate form
        validateForm: async () => {
          return await methods.trigger()
        },

        // Validate form silently (do not keep newly generated UI validation errors)
        // Optionally accepts a list of field names to validate; if omitted, validates all fields
        validateFormSilently: async (fieldNames?: string[]) => {
          const existingErrors = flattenErrors(formState.errors as Record<string, any>)
          const isValid = await methods.trigger(fieldNames, { shouldFocus: false })
          const latestErrors = flattenErrors(
            methods.formState.errors as Record<string, any>
          )

          const persistentErrors = Object.fromEntries(
            Object.entries(latestErrors).filter(([name]) => name in existingErrors)
          )

          clearErrors()
          Object.entries(persistentErrors).forEach(([name, message]) => {
            setError(name, { type: 'manual', message })
          })

          return isValid
        },

        // Submit form
        submitForm: async () => {
          return await new Promise<{
            values: Record<string, any>
            changes: FormChange[]
          }>(resolve => {
            handleSubmit(data => {
              const filteredData = filterConfiguredValues(data)
              const transformedData = applyAllTransforms(filteredData)
              resolve({
                values: transformedData,
                changes: getFormChanges()
              })
            })()
          })
        },

        // Set field value
        setFieldValue: (
          name: string,
          value: any,
          { shouldValidate = true }: { shouldValidate?: boolean } = {
            shouldValidate: true
          }
        ) => {
          setValue(name, value, { shouldValidate })
        },

        // Render form fields
        renderConfigFields,

        // Get data source state
        getDataSource: (key: string) => getDataSource(key),

        // Reload data source
        reloadDataSource,

        // Apply linkage rules manually
        applyLinkageRules: (triggerField: string) => {
          applyLinkageRules(triggerField)
        },

        // --- Instruction-driven error handling interfaces ---
        setFieldError: (name: string, message: string | string[]) => {
          const errorMsg = Array.isArray(message) ? message.join('; ') : message
          setError(name, { type: 'manual', message: errorMsg })
        },

        setErrors: (apiErrors: Record<string, string | string[]>) => {
          Object.entries(apiErrors).forEach(([fieldName, messages]) => {
            const errorMsg = Array.isArray(messages)
              ? messages.join('; ')
              : messages
            setError(fieldName, { type: 'manual', message: errorMsg })
          })
        },

        clearErrors: (names?: string | string[]) => {
          clearErrors(names)
        }
      }),
      [
        getFormattedValues,
        getValues,
        handleReset,
        getFormChanges,
        methods,
        flattenErrors,
        formState.errors,
        handleSubmit,
        filterConfiguredValues,
        applyAllTransforms,
        setValue,
        renderConfigFields,
        getDataSource,
        reloadDataSource,
        applyLinkageRules,
        setError,
        clearErrors
      ]
    )

    const internalOnBlur = useCallback(
      (fieldInfo: { name: string; value: any }, values: Record<string, any>) => {
        const fieldConfig = processedConfig.fields.find(f => f.name === fieldInfo.name)
        if (fieldConfig?.formatOnBlur) {
          const formatted = fieldConfig.formatOnBlur(fieldInfo.value)
          if (formatted !== fieldInfo.value) {
            setValue(fieldInfo.name, formatted, { shouldValidate: true })
          }
        }
        if (onBlur) {
          onBlur(fieldInfo, values)
        }
      },
      [processedConfig.fields, setValue, onBlur]
    )

    return (
      <FormProvider {...methods}>
        <Box component='form'>
          <FormContextProvider
            control={control}
            errors={mergedErrors}
            values={allValues}
            readOnly={readOnly}
            originalValues={initialValues}
            dataSources={dataSourcesState}
            reloadDataSource={reloadDataSource}
            onBlur={internalOnBlur}>
            {typeof children === 'function' ? (
              children(renderConfigFields)
            ) : (
              <Grid2 container spacing={gridSpacing} sx={{ width: '100%' }}>
                {renderConfigFields()}
                {children}
              </Grid2>
            )}
          </FormContextProvider>
        </Box>
      </FormProvider>
    )
  }
)

FormBuilder.displayName = 'FormBuilder'

export default FormBuilder
